import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useQueryClient } from '@tanstack/react-query'

import {
  Button,
  CalendarIcon,
  DatePicker,
  DrawerStepper,
  EditIcon,
  Label,
  Select,
  showToast,
  Skeleton,
  Text,
} from '@mercai/clever'

import { CardInfo } from '@/components/card-info'
import { formatLongDate } from '@/helpers'

import { QUERIES_ADMIN, useSupplierOrderEdit } from '@/services'

import type { CardExpectedPaymentDateProps } from './card-expected-payment-date.type'

import { SupplierPaymentPriorityEnum } from '@/types'
import styles from './card-expected-payment-date.module.css'

const CardExpectedPaymentDateDefault = ({
  data,
}: CardExpectedPaymentDateProps) => {
  const { t: tEnums } = useTranslation('enums')
  const { t } = useTranslation('screenSupplyOrderDetail')

  const [isOpen, setIsOpen] = useState(false)

  const [values, setValues] = useState<
    Partial<{
      expectedPaymentDate: string
      paymentPriority: keyof typeof SupplierPaymentPriorityEnum
    }>
  >({
    expectedPaymentDate: data?.payments?.[0]?.expectedPaymentDate || '',
    paymentPriority: data?.payments?.[0]?.paymentPriority,
  })

  const queryClient = useQueryClient()

  const { mutateAsync } = useSupplierOrderEdit(data?.uuid)

  const isEditable = true

  const onChangeValues = (key: string, value: string | number) => {
    setValues((oldValues) => ({
      ...oldValues,
      [key]: value,
    }))
  }

  const handleClose = () => {
    setIsOpen(false)
  }

  const onSubmit = async () => {
    const response = await mutateAsync({
      uuid: data?.uuid,
      payments: [
        {
          uuid: data?.payments?.[0]?.uuid,
          expectedPaymentDate: values?.expectedPaymentDate,
          paymentPriority: values?.paymentPriority,
        },
      ],
    })

    if (response.success) {
      queryClient.invalidateQueries({
        queryKey: [QUERIES_ADMIN.SUPPLIER_ORDER.module, data?.uuid],
      })
      showToast({
        variant: 'success',
        title: t('tabGeneralCardExpectedPaymentDateSuccessMessageTitle'),
        content: t(
          'tabGeneralCardExpectedPaymentDateSuccessMessageDescription',
        ),
      })

      setIsOpen(false)
    } else {
      showToast({
        variant: 'error',
        title:
          response?.message ||
          t('tabGeneralCardExpectedPaymentDateErrorMessageDescription'),
      })
    }
  }

  useEffect(() => {
    if (!isOpen) return setValues({})
    setValues({
      expectedPaymentDate: data?.payments?.[0]?.expectedPaymentDate || '',
      paymentPriority: data?.payments?.[0]?.paymentPriority,
    })
  }, [isOpen])

  return (
    <div>
      <CardInfo
        icon={<CalendarIcon />}
        title={t('tabGeneralCardExpectedPaymentDateTitle')}
        className={styles.core}
        actions={
          isEditable && (
            <Button
              variant="ghost"
              className={styles.edit}
              size="small"
              onClick={() => setIsOpen(true)}
            >
              {t('editButton')}
              <EditIcon />
            </Button>
          )
        }
      >
        <div>
          <div>
            <Text variant="text-base">
              <strong>
                {data?.payments?.[0]?.expectedPaymentDate
                  ? formatLongDate(data?.payments?.[0]?.expectedPaymentDate)
                  : t('notInformedLabel')}
              </strong>
            </Text>
          </div>

          <div>
            <Text variant="text-base">
              <strong>
                {t('tabGeneralCardExpectedPaymentDatePaymentPriority')}:{' '}
              </strong>
              {data?.payments?.[0]?.paymentPriority
                ? tEnums(
                    `supply_payment_priority_${data?.payments?.[0]?.paymentPriority}`,
                  )
                : t('notInformedLabel')}
            </Text>
          </div>
        </div>
      </CardInfo>

      <DrawerStepper
        isOpen={isOpen}
        onHandleOpen={handleClose}
        initialStep="root"
        title={t('tabGeneralCardExpectedPaymentDateDrawerTitle')}
        translations={{
          optional: '',
        }}
        steps={{
          root: {
            backNavigate: 'root',
            isOptional: false,
            component: (
              <div className={styles['drawer-content']}>
                <div>
                  <Label>
                    {t(
                      'tabGeneralCardExpectedPaymentDateDrawerExpectedPaymentDateLabel',
                    )}
                  </Label>
                  <DatePicker
                    value={values?.expectedPaymentDate}
                    placeholder="DD/MM/AAAA"
                    onChange={(val) =>
                      onChangeValues('expectedPaymentDate', val as string)
                    }
                  />
                </div>

                <div>
                  <Label>
                    {t(
                      'tabGeneralCardExpectedPaymentDateDrawerPaymentPriorityLabel',
                    )}
                  </Label>
                  <Select
                    allowEmpty
                    placeholder={t('selectPlaceholder')}
                    value={values?.paymentPriority}
                    onChange={(val) =>
                      onChangeValues('paymentPriority', val as string)
                    }
                    options={Object.entries(SupplierPaymentPriorityEnum).map(
                      ([key, value]) => ({
                        label: tEnums(`supply_payment_priority_${value}`),
                        value: key,
                      }),
                    )}
                  />
                </div>
              </div>
            ),
            footer: (
              <div className={styles['drawer-footer']}>
                <Button variant="secondary" onClick={handleClose}>
                  {t('cancelButton')}
                </Button>
                <Button variant="primary" onClick={onSubmit}>
                  {t('confirmButton')}
                </Button>
              </div>
            ),
          },
        }}
      />
    </div>
  )
}

const CardExpectedPaymentDateLoading = () => {
  const { t } = useTranslation('screenSupplyOrderDetail')
  return (
    <CardInfo
      icon={<CalendarIcon />}
      title={t('tabGeneralCardExpectedPaymentDateTitle')}
      className={styles.core}
    >
      <Skeleton width="60%" height="1rem" />
    </CardInfo>
  )
}

export const CardExpectedPaymentDate = (
  props: CardExpectedPaymentDateProps,
) => {
  if (props.isLoading) {
    return <CardExpectedPaymentDateLoading />
  }

  return <CardExpectedPaymentDateDefault {...props} />
}
